import type { DraftSender } from "./inboxDraft.types";
import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";

/** The sender/channel pick the GenerateBar panel emits. */
export interface SenderChannelPick {
	userId: number;
	channel: "email" | "linkedin";
	emailId?: number;
}

interface ResolveSenderChannelOptionArgs {
	pick: SenderChannelPick;
	senderOptions: SenderRecipientOption[];
	/** The sender currently on the active draft, if there is one. */
	currentFrom?: DraftSender;
}

/**
 * The sender option matching the pick — exact mailbox first, then any mailbox of that user. A
 * disabled option (a mailbox that needs a reconnect) is never matched (PREC-4312).
 */
const findMatch = (
	{ userId, channel, emailId }: SenderChannelPick,
	allOptions: SenderRecipientOption[]
): SenderRecipientOption | undefined => {
	const senderOptions = allOptions.filter((o) => !o.disabled);

	return channel === "email"
		? (senderOptions.find(
				(o) => o.type === "email" && o.userId === userId && o.emailId === emailId
			) ?? senderOptions.find((o) => o.type === "email" && o.userId === userId))
		: senderOptions.find((o) => o.type === "linkedin" && o.userId === userId);
};

/**
 * Whether the draft already sends as this sender on this channel. The GenerateBar picks a user and
 * a channel, never a mailbox, so another mailbox of the same sender is not a change: a reply on a
 * thread's secondary mailbox stays there when the panel opens (PREC-4312, E4). A draft whose own
 * mailbox can no longer send (disabled, or no longer offered) is not "selected": the pick repairs it.
 */
const isAlreadySelected = (
	option: SenderRecipientOption,
	senderOptions: SenderRecipientOption[],
	currentFrom?: DraftSender
): boolean =>
	currentFrom?.type === option.type &&
	currentFrom.userId === option.userId &&
	(option.type !== "email" ||
		senderOptions.some(
			(o) =>
				o.type === "email" &&
				o.userId === currentFrom.userId &&
				o.emailId === currentFrom.emailId &&
				!o.disabled
		));

/**
 * The sender option a GenerateBar sender/channel pick should apply to the drafts, or `null`.
 *
 * `null` means "do nothing": no sender options are loaded yet, the pick names a sender that is
 * not among them, or the active draft already sends from it — re-applying that last one would
 * rewrite every draft in the thread for no change.
 */
export const resolveSenderChannelOption = ({
	pick,
	senderOptions,
	currentFrom,
}: ResolveSenderChannelOptionArgs): SenderRecipientOption | null => {
	const match = findMatch(pick, senderOptions);

	if (!match || isAlreadySelected(match, senderOptions, currentFrom)) {
		return null;
	}

	return match;
};
